import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { InternalNameIcon } from '@/components/icons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { Agent } from '@/types'
import { agentCategory } from '@/types'
import { AgentActionsMenu } from './AgentActionsMenu'

const NEUTRAL_BORDER = 'rgba(118,124,150,1)'

const headerClass = 'px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground'

interface AgentsListTableProps {
  projectId: string
  agents: Agent[]
  label: string
}

function AgentListRow({ projectId, agent }: { projectId: string; agent: Agent }) {
  const navigate = useNavigate()

  return (
    <tr
      className="group cursor-pointer border-b border-border transition-colors hover:bg-muted has-[[data-state=open]]:bg-muted"
      data-testid={`agent-row-${agent.name}`}
      onClick={() => navigate(`/projects/${projectId}/workflows/agents/${agent.id}`)}
    >
      <td className="px-4 py-3">
        <span className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-card-foreground">
          {agent.displayName}
        </span>
        {agent.latestVersion && (
          <Badge variant="outline" className="ml-2 text-xs" style={{ borderColor: NEUTRAL_BORDER }}>
            V{agent.latestVersion}
          </Badge>
        )}
      </td>

      <td className="px-4 py-3">
        <Badge variant="outline" className="text-xs" style={{ borderColor: NEUTRAL_BORDER }}>
          {agentCategory(agent)}
        </Badge>
      </td>

      <td className="px-4 py-3">
        <span className="flex items-center gap-1.5 font-mono text-sm text-card-foreground">
          <InternalNameIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {agent.name}
        </span>
      </td>

      <td className="px-4 py-3 text-sm text-card-foreground">
        {agent.memoryEnabled ? 'Project' : 'None'}
      </td>

      <td className="max-w-[240px] px-4 py-3">
        {agent.description ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="truncate text-sm text-card-foreground">{agent.description}</p>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p>{agent.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-sm text-card-foreground">—</span>
        )}
      </td>

      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-end">
          <AgentActionsMenu projectId={projectId} agent={agent} />
        </div>
      </td>
    </tr>
  )
}

export function AgentsListTable({ projectId, agents, label }: AgentsListTableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-muted" data-testid="agents-list-table">
      <table className="w-full break-words text-left" aria-label={label}>
        <thead>
          <tr className="border-b border-border bg-table-header">
            <th className={headerClass}>Agent Name</th>
            <th className={headerClass}>Type</th>
            <th className={headerClass}>Internal Name</th>
            <th className={headerClass}>Memory</th>
            <th className={headerClass}>Description</th>
            <th className={`${headerClass} text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {agents.map(agent => (
            <AgentListRow key={agent.id} projectId={projectId} agent={agent} />
          ))}
        </tbody>
      </table>
    </div>
  )
}
